import * as React from "react";
import { useNotify } from "react-admin";
import {
  Box, Button, Checkbox, CircularProgress, FormControl, InputLabel,
  MenuItem, Select, Stack, Typography
} from "@mui/material";

const apiUrl = process.env.REACT_APP_API_URL || "/api";

type Team = { id: number; display_name?: string; name?: string };

export default function TrainingTemplateTeamAssignmentsPage() {
  const notify = useNotify();

  const [templates, setTemplates] = React.useState<any[]>([]);
  const [teams, setTeams] = React.useState<Team[]>([]);
  const [templateId, setTemplateId] = React.useState<number | "">("");
  const [selectedTeamIds, setSelectedTeamIds] = React.useState<number[]>([]);
  const [loading, setLoading] = React.useState(false);

  const token = (window as any).__keycloak?.token; // falls du Keycloak global hast
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [tRes, teamRes] = await Promise.all([
          fetch(`${apiUrl}/training-templates`, { headers }),
          fetch(`${apiUrl}/teams`, { headers }),
        ]);
        const tJson = await tRes.json();
        const teamJson = await teamRes.json();

        setTemplates(tJson?.data ?? tJson); // je nach API-Format
        setTeams(teamJson?.data ?? teamJson);
      } catch (e: any) {
        notify(e?.message ?? "Fehler beim Laden", { type: "error" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!templateId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${apiUrl}/training-templates/${templateId}/teams`, { headers });
        const json = await res.json();
        setSelectedTeamIds(json.team_ids ?? []);
      } catch (e: any) {
        notify(e?.message ?? "Fehler beim Laden der Zuordnung", { type: "error" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const toggle = (id: number) => {
    setSelectedTeamIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const save = async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/training-templates/${templateId}/teams`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ team_ids: selectedTeamIds }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail ?? "Speichern fehlgeschlagen");
      }
      notify("Zuordnung gespeichert.", { type: "info" });
    } catch (e: any) {
      notify(e?.message ?? "Fehler beim Speichern", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Typography variant="h5">Training ↔ Teams zuordnen</Typography>

        <FormControl fullWidth>
          <InputLabel id="tmpl">Training Template</InputLabel>
          <Select
            labelId="tmpl"
            label="Training Template"
            value={templateId}
            onChange={(e) => setTemplateId(Number(e.target.value))}
          >
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!templateId ? (
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Wähle ein Template aus.
          </Typography>
        ) : (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Erlaubte Teams (leer = alle Teams dürfen spielen)
            </Typography>

            <Stack spacing={1}>
              {teams.map((tm) => {
                const label = tm.display_name ?? tm.name ?? `Team ${tm.id}`;
                const checked = selectedTeamIds.includes(tm.id);
                return (
                  <Box key={tm.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox checked={checked} onChange={() => toggle(tm.id)} />
                    <Typography>{label}</Typography>
                  </Box>
                );
              })}
            </Stack>

            <Button sx={{ mt: 2 }} variant="contained" onClick={save} disabled={loading}>
              Speichern
            </Button>
          </Box>
        )}

        {loading && <CircularProgress size={22} />}
      </Stack>
    </Box>
  );
}
